/**
 * src/components/common/PWAInstallBanner.jsx
 * Shows:
 *   - "Add to Home Screen" banner when installable
 *   - Offline indicator strip when network is lost
 *   - "New version available" banner on SW update
 *
 * Usage — add inside App.jsx return (anywhere near the root):
 *   import PWAInstallBanner from './components/common/PWAInstallBanner';
 *   <PWAInstallBanner />
 */
import { useState } from 'react';
import { usePWA } from '../../hooks/usePWA';

export default function PWAInstallBanner() {
  const { isInstallable, install, isOnline, swUpdateAvailable } = usePWA();
  const [dismissed, setDismissed] = useState(false);

  const handleInstall = async () => {
    const outcome = await install();
    if (outcome === 'dismissed') setDismissed(true);
  };

  const handleUpdate = () => window.location.reload();

  return (
    <>
      {/* ── Offline Indicator ─────────────────────────── */}
      {!isOnline && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
          background: '#dc2626', color: '#fff',
          textAlign: 'center', fontSize: 12, fontWeight: 600,
          padding: `max(env(safe-area-inset-top,0px),8px) 16px 8px`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <span>📡</span> You are offline — showing cached data
        </div>
      )}

      {/* ── SW Update Banner ──────────────────────────── */}
      {swUpdateAvailable && (
        <div style={{
          position: 'fixed', bottom: 80, left: 12, right: 12, zIndex: 1100,
          background: '#1a56db', color: '#fff',
          borderRadius: 12, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 8px 32px rgba(26,86,219,.4)',
          animation: 'slide-up .3s ease both',
        }}>
          <span style={{ fontSize: 22 }}>🆕</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>New version available</div>
            <div style={{ fontSize: 11, opacity: .8 }}>Refresh to get the latest features</div>
          </div>
          <button onClick={handleUpdate}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none',
              background: '#fff', color: '#1a56db', fontSize: 12,
              fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Refresh
          </button>
        </div>
      )}

      {/* ── Install Banner ────────────────────────────── */}
      {isInstallable && !dismissed && (
        <div id="ia-install-banner">
          <div style={{ fontSize: 32, flexShrink: 0 }}>📲</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Install iAssets</div>
            <div style={{ fontSize: 11, opacity: .8 }}>
              Add to Home Screen for faster access &amp; offline use
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setDismissed(true)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.4)',
                background: 'transparent', color: '#fff', fontSize: 11, cursor: 'pointer' }}>
              Later
            </button>
            <button onClick={handleInstall}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none',
                background: '#fff', color: '#1a56db', fontSize: 12,
                fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Install
            </button>
          </div>
        </div>
      )}
    </>
  );
}
